import type { GridFormulaFeature } from '../../../models/gridFeatureDependencies';
import type { DataGridPremiumProps } from '../../../models/dataGridPremiumProps';
import { useGridFormula, formulaStateInitializer } from './useGridFormula';
import { useGridFormulaPreProcessors } from './useGridFormulaPreProcessors';
import { useGridFormulaColumnHeaderAdornment } from './useGridFormulaColumnHeaderAdornment';
import { GRID_FORMULA_ROW_NUMBER_FIELD } from './gridFormulaPositionContext';
import { GridFormulaReferenceOverlay } from '../../../components/GridFormulaReferenceOverlay';
import { FormulaBar } from '../../../components/formulaBar';

type GridPinnedColumnsModel = { left?: string[]; right?: string[] } | undefined;

function pinRowNumberColumnLeft(model: GridPinnedColumnsModel) {
  const left = model?.left ?? [];
  if (left.includes(GRID_FORMULA_ROW_NUMBER_FIELD)) {
    return model;
  }
  return { ...model, left: [GRID_FORMULA_ROW_NUMBER_FIELD, ...left] };
}

/**
 * When A1 notation is active, pins the autogenerated row-number column to the
 * left so it stays visible while scrolling — a spreadsheet affordance. Pinning
 * is purely model-driven (no utility-column special-casing exists), so the field
 * is merged into the controlled `pinnedColumns` model, or the uncontrolled
 * `initialState.pinnedColumns` seed.
 *
 * Trade-off (documented in the formula tech doc): the synthetic field surfaces
 * in `onPinnedColumnsChange` payloads and `exportState()`. It is harmless on
 * restore — the columns layer drops fields with no matching column.
 */
function getFormulaA1PinningOverride(themedProps: DataGridPremiumProps) {
  const a1Active =
    !!themedProps.formulaA1Notation && !themedProps.disableFormulas && !themedProps.dataSource;
  if (!a1Active) {
    return null;
  }
  if (themedProps.pinnedColumns) {
    return { pinnedColumns: pinRowNumberColumnLeft(themedProps.pinnedColumns) };
  }
  return {
    initialState: {
      ...themedProps.initialState,
      pinnedColumns: pinRowNumberColumnLeft(themedProps.initialState?.pinnedColumns),
    },
  };
}

/**
 * The formula feature runtime, to be passed to the grid through the
 * `featureDependencies` prop:
 *
 * ```tsx
 * import { DataGridPremium } from '@mui/x-data-grid-premium';
 * import { formulaFeature } from '@mui/x-data-grid-premium/formula';
 *
 * <DataGridPremium featureDependencies={{ formula: formulaFeature }} />;
 * ```
 *
 * The grid itself only references the feature at the type level, so the formula
 * engine, editor and components are only bundled when this object is imported.
 */
export const formulaFeature: GridFormulaFeature = {
  stateInitializer: formulaStateInitializer,
  usePreProcessors: useGridFormulaPreProcessors,
  useFeature: useGridFormula,
  useColumnHeaderAdornment: useGridFormulaColumnHeaderAdornment,
  ReferenceOverlay: GridFormulaReferenceOverlay,
  FormulaBar,
  transformProps: getFormulaA1PinningOverride,
};
